import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Bot, Save, Loader2, PoundSterling, Clock, Zap, ShieldAlert } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const TONES = [
  { v: "friendly", l: "Friendly & warm" },
  { v: "professional", l: "Professional & formal" },
  { v: "cheeky", l: "Cheeky & fun" },
  { v: "calm", l: "Calm & reassuring" },
];
const HOURS = Array.from({ length: 24 }, (_, h) => ({ v: String(h), l: `${((h % 12) || 12)}:00 ${h < 12 ? "AM" : "PM"}` }));

export default function Settings() {
  const { user } = useAuth();
  const { company, reloadCompany } = useOutletContext();
  const [form, setForm] = useState({
    name: company?.name || "", description: company?.description || "",
    ava_tone: company?.ava_tone || "friendly",
    phone: company?.phone || "", area: company?.area || "",
    website_url: company?.website_url || "", facebook_url: company?.facebook_url || "",
    instagram_url: company?.instagram_url || "", twitter_url: company?.twitter_url || "", linkedin_url: company?.linkedin_url || "",
  });
  const [workDays, setWorkDays] = useState(company?.work_days || [0, 1, 2, 3, 4]);
  const [workStart, setWorkStart] = useState(String(company?.work_start_hour ?? 8));
  const [workEnd, setWorkEnd] = useState(String(company?.work_end_hour ?? 17));
  const [slotInterval, setSlotInterval] = useState(String(company?.slot_interval_hours ?? 3));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const isOwner = !!company?.free_access;
  const [owner, setOwner] = useState(null);
  const [ownerForm, setOwnerForm] = useState({ balance_credits: "", threshold: 500 });
  const [savingOwner, setSavingOwner] = useState(false);

  useEffect(() => {
    if (!isOwner) return;
    api.get("/owner/credits").then((r) => {
      setOwner(r.data);
      setOwnerForm({ balance_credits: r.data.balance_credits ?? "", threshold: r.data.threshold ?? 500 });
    }).catch(() => {});
  }, [isOwner]);

  const saveOwner = async () => {
    setSavingOwner(true);
    try {
      const r = await api.post("/owner/credits", {
        balance_credits: Number(ownerForm.balance_credits) || 0,
        threshold: Number(ownerForm.threshold) || 0,
      });
      setOwner(r.data);
      toast.success("Platform balance updated");
    } catch { toast.error("Could not save"); }
    finally { setSavingOwner(false); }
  };

  const toggleDay = (d) => setWorkDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort());

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/company/me", {
        ...form,
        work_days: workDays,
        work_start_hour: Number(workStart),
        work_end_hour: Number(workEnd),
        slot_interval_hours: Number(slotInterval),
      });
      await reloadCompany();
      toast.success("Settings updated — Ava adapts instantly");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const field = (k, label, ph) => (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">{label}</Label>
      <Input data-testid={`settings-${k}`} value={form[k]} onChange={set(k)} placeholder={ph} className="rounded-sm bg-[#0f0f11] border-white/10" />
    </div>
  );

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight">SETTINGS</h1>
        <p className="text-muted-foreground mt-1">Update your business profile — Ava adapts instantly.</p>
      </div>

      <div className="bg-[#18181b] border border-white/10 rounded-sm p-6">
        <h3 className="font-display text-xl tracking-wide mb-4">ACCOUNT</h3>
        <div className="flex items-center gap-4">
          {user?.picture ? <img src={user.picture} alt="" className="h-14 w-14 rounded-sm object-cover" /> : <div className="h-14 w-14 rounded-sm bg-secondary flex items-center justify-center font-display text-2xl">{user?.name?.[0]}</div>}
          <div><p className="font-medium text-lg">{user?.name}</p><p className="text-sm text-muted-foreground">{user?.email}</p></div>
        </div>
      </div>

      <div className="bg-[#18181b] border border-white/10 rounded-sm p-6 space-y-5">
        <h3 className="font-display text-xl tracking-wide">BUSINESS PROFILE</h3>
        {field("name", "Company Name")}
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Describe your business & services</Label>
          <Textarea data-testid="settings-description" value={form.description} onChange={set("description")} placeholder="Explain what your business does in as much detail as you like — the services you offer, the kind of jobs you take on, what makes you different. Ava uses this (plus her own knowledge of your industry) to answer customers and ask the right questions." className="rounded-sm bg-[#0f0f11] border-white/10 min-h-[160px]" />
          <p className="text-xs text-muted-foreground mt-1.5">The more detail you give, the smarter Ava is. She'll also draw on general knowledge of your industry to ask the right questions.</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Ava's tone of voice</Label>
          <Select value={company?.is_pro === false ? "friendly" : form.ava_tone} onValueChange={(v) => setForm({ ...form, ava_tone: v })} disabled={company?.is_pro === false}>
            <SelectTrigger data-testid="settings-tone" className="rounded-sm bg-[#0f0f11] border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#18181b] border-white/10 text-foreground">
              {TONES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1.5">{company?.is_pro === false ? "Lite uses the default friendly tone. Upgrade to Pro to unlock all Ava tones." : "How Ava comes across to your customers."}</p>
        </div>
        <div className="grid grid-cols-2 gap-4">{field("phone", "Phone")}{field("area", "Service Area")}</div>
      </div>

      <div className="bg-[#18181b] border border-white/10 rounded-sm p-6 space-y-4">
        <div>
          <h3 className="font-display text-xl tracking-wide flex items-center gap-2"><Clock className="h-5 w-5 text-primary" /> WORKING HOURS</h3>
          <p className="text-xs text-muted-foreground mt-1">Ava only offers callers slots within these days and hours.</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Working days</Label>
          <div className="flex flex-wrap gap-2">
            {DAYS.map(([label, d]) => (
              <button key={d} type="button" data-testid={`day-${d}`} onClick={() => toggleDay(d)}
                className={`px-3 py-2 rounded-sm text-sm font-medium border transition-[background-color,border-color] duration-150 ${workDays.includes(d) ? "bg-primary text-white border-primary" : "bg-[#0f0f11] text-muted-foreground border-white/10 hover:border-white/30"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Day starts</Label>
            <Select value={workStart} onValueChange={setWorkStart}>
              <SelectTrigger data-testid="work-start" className="rounded-sm bg-[#0f0f11] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#18181b] border-white/10 text-foreground max-h-72">{HOURS.map((h) => <SelectItem key={h.v} value={h.v}>{h.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Day ends</Label>
            <Select value={workEnd} onValueChange={setWorkEnd}>
              <SelectTrigger data-testid="work-end" className="rounded-sm bg-[#0f0f11] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#18181b] border-white/10 text-foreground max-h-72">{HOURS.map((h) => <SelectItem key={h.v} value={h.v}>{h.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Slot every</Label>
            <Select value={slotInterval} onValueChange={setSlotInterval}>
              <SelectTrigger data-testid="slot-interval" className="rounded-sm bg-[#0f0f11] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#18181b] border-white/10 text-foreground">{[1, 2, 3, 4].map((i) => <SelectItem key={i} value={String(i)}>{i} hour{i > 1 ? "s" : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="bg-[#18181b] border border-white/10 rounded-sm p-6 space-y-5">
        <h3 className="font-display text-xl tracking-wide">WEBSITE & SOCIAL LINKS</h3>
        {field("website_url", "Website", "https://yourbusiness.co.uk")}
        <div className="grid grid-cols-2 gap-4">
          {field("facebook_url", "Facebook", "https://facebook.com/...")}
          {field("instagram_url", "Instagram", "https://instagram.com/...")}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {field("twitter_url", "X / Twitter", "https://x.com/...")}
          {field("linkedin_url", "LinkedIn", "https://linkedin.com/...")}
        </div>
      </div>

      <Button data-testid="settings-save" onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-orange-600 text-white font-semibold gap-2 h-11 px-6">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
      </Button>

      {isOwner && (
        <div className="bg-[#141416] border border-primary/30 rounded-sm p-6 space-y-5" data-testid="owner-platform-panel">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-sm bg-primary/15 flex items-center justify-center"><ShieldAlert className="h-5 w-5 text-primary" /></div>
            <div>
              <h3 className="font-display text-xl tracking-wide">PLATFORM · OWNER ONLY</h3>
              <p className="text-xs text-muted-foreground">Only you can see this. Track your Emergent credit balance across every business.</p>
            </div>
          </div>

          {owner && (
            <div className={`rounded-sm p-4 border ${owner.low ? "border-red-500/40 bg-red-500/5" : "border-white/10 bg-[#0f0f11]"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Estimated balance remaining</p>
                  <p className="font-display text-4xl tracking-tight mt-1" data-testid="owner-remaining">{owner.remaining ?? "—"}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Used since last set</p>
                  <p className="font-display text-2xl tracking-tight mt-1 text-muted-foreground">{owner.consumed_since}</p>
                </div>
              </div>
              {owner.low && (
                <p className="flex items-center gap-2 text-sm text-red-400 mt-3"><Zap className="h-4 w-4" /> Low — top up your Emergent balance, then update the figure below.</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Your current Emergent balance (credits)</Label>
              <Input data-testid="owner-balance-input" type="number" step="any" value={ownerForm.balance_credits} onChange={(e) => setOwnerForm({ ...ownerForm, balance_credits: e.target.value })} placeholder="22.83" className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Alert me when below</Label>
              <Input data-testid="owner-threshold-input" type="number" step="any" value={ownerForm.threshold} onChange={(e) => setOwnerForm({ ...ownerForm, threshold: e.target.value })} placeholder="500" className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Set this to your real Emergent credit balance each time you top up. Usage across all businesses is subtracted from it, and you'll get an in-app + push alert when it drops below your threshold.</p>
          <Button data-testid="owner-save-btn" onClick={saveOwner} disabled={savingOwner} className="rounded-sm bg-primary hover:bg-orange-600 text-white font-semibold gap-2 h-11 px-6">
            {savingOwner ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Update Balance
          </Button>
        </div>
      )}

      <div className="bg-[#18181b] border border-white/10 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-9 w-9 bg-primary rounded-sm flex items-center justify-center"><Bot className="h-5 w-5 text-white" /></div>
          <h3 className="font-display text-xl tracking-wide">RECEPTIONIST</h3>
        </div>
        <p className="text-sm text-muted-foreground">Ava is powered by GPT-5.5 and tuned to your business from the description above. She greets customers as {company?.name}, qualifies leads, and captures booking requests for you to approve.</p>
      </div>
    </div>
  );
}
